import { useRef, useState } from "react";
import {
  FlipVertical2,
  Image as ImageIcon,
  ImageOff,
  Loader2,
  RotateCw,
  Trash2,
  Upload,
} from "lucide-react";

import {
  ACCEPTED_IMAGE_MIME,
  deleteImage,
  imageUrl,
  rotateImage,
  uploadImage,
} from "@/features/imageUpload";

interface Props {
  /** /api/images id stored on a Card (imageFrontRef or imageBackRef). */
  imageRef?: string;
  alt: string;
  /** Optional className applied to the wrapper. */
  className?: string;
  /** When set, the image scales to fill this Tailwind aspect-ratio
   *  utility instead of letting natural dimensions take over. */
  fit?: "card" | "square" | "free";
}

const cardAspect = "aspect-[5/7]"; // standard TCG card ratio (2.5" × 3.5")
const squareAspect = "aspect-square";

/**
 * Renders a card photo from its /api/images id. Falls back to a small
 * placeholder when no ref is provided so the layout stays stable while
 * the seller hasn't uploaded a photo yet. The browser handles loading +
 * caching — the image endpoint sets `Cache-Control: immutable`.
 */
export function CardImage({ imageRef, alt, className, fit = "card" }: Props) {
  const url = imageUrl(imageRef);
  const aspect =
    fit === "card" ? cardAspect : fit === "square" ? squareAspect : "";
  const base =
    `relative overflow-hidden rounded-md border border-border bg-muted ${aspect} ${className ?? ""}`.trim();

  if (!url) {
    return (
      <div
        className={`${base} flex flex-col items-center justify-center text-muted-foreground`}
        aria-label={`${alt} — no photo`}
      >
        <ImageOff className="h-6 w-6 opacity-60" />
        <span className="mt-1 text-[10px]">No photo</span>
      </div>
    );
  }
  return (
    <div className={base}>
      <img
        src={url}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-contain"
      />
    </div>
  );
}

interface UploaderProps {
  label: string;
  imageRef: string | undefined;
  onChange: (newRef: string | undefined) => void;
  /** Forwarded to the preview CardImage. Defaults to "card" (5:7). */
  fit?: "card" | "square" | "free";
  /** Optional helper text rendered under the label. */
  hint?: string;
  /** Mobile camera capture hint on the file input. Defaults to true (good
   *  for card photos); pass false for logo/watermark uploads. */
  captureFromCamera?: boolean;
}

/**
 * Single-image upload + preview + remove control. Pairs a CardImage
 * preview with a file picker; replacing or removing the image silently
 * deletes the old blob from /api/images. Errors render inline.
 */
export function CardImageUploader({
  label,
  imageRef,
  onChange,
  fit = "card",
  hint,
  captureFromCamera = true,
}: UploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = uploading || rotating;
  const onPick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const id = await uploadImage(file);
      // Delete the old blob (if any) after the new one is safely uploaded
      // so a failed upload doesn't lose the existing photo.
      if (imageRef) {
        void deleteImage(imageRef);
      }
      onChange(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onRotate = async (degrees: 90 | 180 | 270) => {
    if (!imageRef) return;
    setError(null);
    setRotating(true);
    try {
      const newId = await rotateImage(imageRef, degrees);
      onChange(newId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  const onRemove = () => {
    if (imageRef) {
      void deleteImage(imageRef);
    }
    onChange(undefined);
  };

  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium tracking-wide text-muted-foreground">
        {label}
      </div>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      <CardImage
        imageRef={imageRef}
        alt={label}
        fit={fit}
        className="w-full max-w-[180px]"
      />
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_MIME}
        {...(captureFromCamera ? { capture: "environment" as const } : {})}
        onChange={(e) => void onFile(e)}
        className="hidden"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onPick}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : imageRef ? (
            <Upload className="h-3 w-3" />
          ) : (
            <ImageIcon className="h-3 w-3" />
          )}
          {uploading ? "Uploading…" : imageRef ? "Replace" : "Upload"}
        </button>
        {imageRef && (
          <>
            <button
              type="button"
              onClick={() => void onRotate(90)}
              disabled={busy}
              aria-label="Rotate 90° clockwise"
              title="Rotate 90° clockwise"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
            >
              {rotating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCw className="h-3 w-3" />
              )}
              90°
            </button>
            <button
              type="button"
              onClick={() => void onRotate(180)}
              disabled={busy}
              aria-label="Flip 180°"
              title="Flip 180°"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
            >
              <FlipVertical2 className="h-3 w-3" />
              180°
            </button>
            {!busy && (
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-destructive hover:bg-muted"
              >
                <Trash2 className="h-3 w-3" />
                Remove
              </button>
            )}
          </>
        )}
      </div>
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}
